'use client';

import { useConfirmDialog } from '@/components/confirm/ConfirmDialogProvider';
import { buildAuthHref } from '@/features/auth/utils/authRedirect';
import EmptyState from '@/features/empty-state/EmptyState';
import { clientFetch } from '@/lib/api/clientFetch';
import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState, useTransition } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import CustomersSayCard from './components/CustomersSayCard';
import ReviewActionPanel from './components/ReviewActionPanel';
import ReviewCard from './components/ReviewCard';
import ReviewModal from './components/ReviewModal';
import ReviewPagination from './components/ReviewPagination';
import './components/_customersSayCard.scss';
import './components/_reviewCard.scss';
import './components/_reviewModal.scss';
import './components/_reviewPagination.scss';
import './productReviews.scss';
import {
  getMarketplaceDate,
  getReviewCountCopy,
  normalizeReviewData,
  readMessage,
  resolveInitialOwnReviewState,
} from './reviewUtils';

export default function ProductReviewsSection({
  currentUser,
  initialOwnReviewState = null,
  initialReviews = null,
  messages = {},
  product,
}) {
  const router = useRouter();
  const confirm = useConfirmDialog();
  const sectionRef = useRef(null);
  const [isRefreshing, startRefreshTransition] = useTransition();
  const [reviewData, setReviewData] = useState(
    normalizeReviewData(initialReviews),
  );
  const [isLoadingReviews, setIsLoadingReviews] = useState(!initialReviews);
  const [reviewsError, setReviewsError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [pendingAction, setPendingAction] = useState('');
  const [pendingHelpfulId, setPendingHelpfulId] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formRating, setFormRating] = useState('');
  const [formComment, setFormComment] = useState('');
  const [formError, setFormError] = useState('');
  const [ownReviewState, setOwnReviewState] = useState(() =>
    resolveInitialOwnReviewState({
      currentUser,
      initialOwnReviewState,
      initialReviews,
    }),
  );

  const productReviewCount = Number(product?.numReviews) || 0;
  const onsiteReviewCount = Number(reviewData.total) || 0;
  const reviewCountCopy = getReviewCountCopy(messages, onsiteReviewCount);
  const customersSayCopy = Array.isArray(product?.customers_say)
    ? product.customers_say.filter(Boolean).join(' ').trim()
    : String(product?.customers_say || '').trim();
  const hasCustomersSay = Boolean(
    (typeof product?.fromAmazon === 'boolean' ? product.fromAmazon : true) &&
    customersSayCopy,
  );
  const hasExternalReviewSignal = productReviewCount > 0 || hasCustomersSay;
  const marketplaceDate = getMarketplaceDate(product?.createdAt);
  const loginHref = buildAuthHref(
    '/login',
    `/product/${product.productNumber}`,
  );
  const editActionLabel = readMessage(
    messages,
    'writeReview.editAction',
    'Edit',
  );
  const deleteActionLabel = readMessage(
    messages,
    'writeReview.deleteAction',
    'Delete',
  );
  const helpfulLabel = messages['reviews.markHelpful'] || 'Mark as helpful';

  useEffect(() => {
    setReviewData(normalizeReviewData(initialReviews));
    setIsLoadingReviews(!initialReviews);
    setReviewsError('');
  }, [initialReviews]);

  useEffect(() => {
    setOwnReviewState(
      resolveInitialOwnReviewState({
        currentUser,
        initialOwnReviewState,
        initialReviews,
      }),
    );
  }, [currentUser?.userNumber, initialOwnReviewState, initialReviews]);

  useEffect(() => {
    if (initialReviews) {
      return undefined;
    }

    let isCancelled = false;

    async function loadInitialReviews() {
      setIsLoadingReviews(true);
      setReviewsError('');

      try {
        const payload = await clientFetch(
          `/api/products/${encodeURIComponent(product.productNumber)}/reviews?page=1`,
        );

        if (isCancelled) {
          return;
        }

        setReviewData(normalizeReviewData(payload));
      } catch (error) {
        if (isCancelled) {
          return;
        }

        setReviewsError(
          error?.data?.message ||
            readMessage(
              messages,
              'reviewsList.error',
              'Failed to load reviews.',
            ),
        );
      } finally {
        if (!isCancelled) {
          setIsLoadingReviews(false);
        }
      }
    }

    loadInitialReviews();

    return () => {
      isCancelled = true;
    };
  }, [initialReviews, messages, product.productNumber]);

  async function loadReviews(pageNumber, options = {}) {
    const nextPage = Math.max(1, Number(pageNumber) || 1);
    const shouldShowLoader = options.showLoader !== false;

    if (shouldShowLoader) {
      setIsLoadingReviews(true);
    }

    setReviewsError('');

    try {
      const payload = await clientFetch(
        `/api/products/${encodeURIComponent(product.productNumber)}/reviews?page=${nextPage}`,
      );
      const normalized = normalizeReviewData(payload);

      if (nextPage > normalized.pages && normalized.pages >= 1) {
        return await loadReviews(normalized.pages, options);
      }

      setReviewData(normalized);
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
      return normalized;
    } catch (error) {
      setReviewsError(
        error?.data?.message ||
          readMessage(messages, 'reviewsList.error', 'Failed to load reviews.'),
      );
      return null;
    } finally {
      if (shouldShowLoader) {
        setIsLoadingReviews(false);
      }
    }
  }

  async function refreshOwnReview() {
    if (!currentUser?.userNumber) {
      setOwnReviewState({
        isReviewed: false,
        hasPurchased: false,
        loading: false,
        review: null,
      });
      return null;
    }

    const payload = await clientFetch(
      `/api/products/${encodeURIComponent(product.productNumber)}/reviews/check`,
    );

    setOwnReviewState({
      isReviewed: Boolean(payload?.isReviewed),
      hasPurchased: Boolean(payload?.hasPurchased || payload?.review),
      loading: false,
      review: payload?.review || null,
    });

    return payload;
  }

  function openReviewModal() {
    setFeedback(null);
    setFormError('');

    if (ownReviewState.isReviewed && ownReviewState.review) {
      setFormRating(String(ownReviewState.review.rating || ''));
      setFormComment(ownReviewState.review.comment || '');
    } else {
      setFormRating('');
      setFormComment('');
    }

    setIsModalOpen(true);
  }

  function closeReviewModal() {
    setIsModalOpen(false);
    setFormError('');
  }

  async function handleHelpfulToggle(review, checked) {
    if (!currentUser?.userNumber || !review?._id) {
      return;
    }

    const previousReviews = reviewData.reviews;

    setPendingHelpfulId(review._id);
    setReviewData((current) => ({
      ...current,
      reviews: current.reviews.map((entry) => {
        if (String(entry._id) !== String(review._id)) {
          return entry;
        }

        return {
          ...entry,
          helpfulCount: Math.max(
            0,
            (Number(entry.helpfulCount) || 0) + (checked ? 1 : -1),
          ),
          isHelpfulByUser: checked,
        };
      }),
    }));

    try {
      await clientFetch(
        `/api/products/${encodeURIComponent(product.productNumber)}/reviews`,
        {
          method: 'PATCH',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            action: 'toggleHelpful',
            reviewId: review._id,
            checked,
          }),
        },
      );
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        readMessage(
          messages,
          'writeReview.error',
          'Could not submit review. Please try again.',
        );

      setReviewData((current) => ({ ...current, reviews: previousReviews }));
      setFeedback({
        message: errorMessage,
        tone: 'error',
      });
      toast.error(errorMessage);
    } finally {
      setPendingHelpfulId('');
    }
  }

  async function handleDeleteReview() {
    const accepted = await confirm({
      title: readMessage(messages, 'writeReview.deleteTitle', 'Delete review'),
      message: readMessage(
        messages,
        'writeReview.deleteConfirm',
        'Delete your review? This cannot be undone.',
      ),
      confirmLabel: readMessage(
        messages,
        'writeReview.deleteConfirmLabel',
        'Delete review',
      ),
      cancelLabel: readMessage(
        messages,
        'writeReview.keepReview',
        'Keep review',
      ),
      confirmTone: 'danger',
    });

    if (!accepted) {
      return;
    }

    setPendingAction('delete');
    setFeedback(null);

    try {
      await clientFetch(
        `/api/products/${encodeURIComponent(product.productNumber)}/reviews`,
        {
          method: 'DELETE',
        },
      );

      await Promise.all([
        loadReviews(reviewData.page, { showLoader: false }),
        refreshOwnReview(),
      ]);

      const successMessage = readMessage(
        messages,
        'writeReview.deleteSuccess',
        'Review deleted',
      );

      setFeedback({
        message: successMessage,
        tone: 'success',
      });
      toast.success(successMessage);
      closeReviewModal();
      startRefreshTransition(() => {
        router.refresh();
      });
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        readMessage(
          messages,
          'writeReview.deleteError',
          'Unable to delete review',
        );

      setFeedback({
        message: errorMessage,
        tone: 'error',
      });
      toast.error(errorMessage);
    } finally {
      setPendingAction('');
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setFeedback(null);
    setFormError('');

    const normalizedRating = Number(formRating);
    const normalizedComment = formComment.trim();

    if (
      !Number.isFinite(normalizedRating) ||
      normalizedRating < 1 ||
      normalizedRating > 5
    ) {
      setFormError(
        readMessage(
          messages,
          'writeReview.ratingRequired',
          'Select a rating before publishing your review.',
        ),
      );
      return;
    }

    if (!normalizedComment) {
      setFormError(
        readMessage(
          messages,
          'writeReview.commentRequired',
          'Add a comment before publishing your review.',
        ),
      );
      return;
    }

    if (!ownReviewState.hasPurchased && !ownReviewState.isReviewed) {
      setFormError(
        readMessage(
          messages,
          'writeReview.mustPurchase',
          'You can review this product only after purchase.',
        ),
      );
      return;
    }

    const isEditing = ownReviewState.isReviewed;
    setPendingAction(isEditing ? 'edit' : 'create');

    try {
      const response = await clientFetch(
        `/api/products/${encodeURIComponent(product.productNumber)}/reviews`,
        {
          method: isEditing ? 'PATCH' : 'POST',
          headers: {
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            rating: normalizedRating,
            comment: normalizedComment,
          }),
        },
      );

      await Promise.all([
        loadReviews(isEditing ? reviewData.page : 1, { showLoader: false }),
        refreshOwnReview(),
      ]);

      setFeedback({
        message:
          response?.message ||
          readMessage(
            messages,
            'writeReview.success',
            'Review submitted successfully.',
          ),
        tone: 'success',
      });
      toast.success(
        response?.message ||
          readMessage(
            messages,
            'writeReview.success',
            'Review submitted successfully.',
          ),
      );
      closeReviewModal();
      startRefreshTransition(() => {
        router.refresh();
      });
    } catch (error) {
      const errorMessage =
        error?.data?.message ||
        readMessage(
          messages,
          'writeReview.error',
          'Could not submit review. Please try again.',
        );

      setFormError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setPendingAction('');
    }
  }

  const visibleReviews = reviewData.reviews.filter(
    (review) =>
      String(review?._id) !== String(ownReviewState.review?._id || ''),
  );
  const hasVisibleReviews = visibleReviews.length > 0;
  const hasOwnReview = Boolean(ownReviewState.review);
  const canWriteReview = Boolean(
    currentUser?.userNumber && ownReviewState.hasPurchased,
  );
  const shouldShowEmptyPanel =
    !hasVisibleReviews && !hasOwnReview && canWriteReview;
  const hasOnsitePanel =
    isLoadingReviews ||
    hasVisibleReviews ||
    hasOwnReview ||
    Boolean(reviewsError);

  const actionPanel =
    canWriteReview || hasOwnReview ? (
      <ReviewActionPanel
        currentUser={currentUser}
        deleteActionLabel={deleteActionLabel}
        editActionLabel={editActionLabel}
        helpfulLabel={helpfulLabel}
        isRefreshing={isRefreshing}
        loginHref={loginHref}
        messages={messages}
        onDeleteOwnReview={handleDeleteReview}
        onEditOwnReview={openReviewModal}
        ownReviewState={ownReviewState}
        pendingHelpfulId={pendingHelpfulId}
      />
    ) : null;

  const marketplacePanel = hasCustomersSay ? (
    <CustomersSayCard
      customersSayCopy={customersSayCopy}
      marketplaceDate={marketplaceDate}
      messages={messages}
      rating={product?.product_star_rating}
    />
  ) : null;

  const onsitePanel = hasOnsitePanel ? (
    <div className="product-reviews__main">
      <div
        className="product-review-card
       product-reviews__main-header product-detail-panel__summary-card"
      >
        <h3 className="product-reviews__main-title">
          {onsiteReviewCount > 0
            ? getReviewCountCopy(messages, onsiteReviewCount)
            : readMessage(
                messages,
                'reviewsList.firstReviewTitle',
                'Be the first to review this product',
              )}
        </h3>
        <p className="product-reviews__main-copy">
          {readMessage(
            messages,
            'reviewsList.onsiteCopy',
            'Feedback from DakarGameHub shoppers.',
          )}
        </p>
      </div>

      {isLoadingReviews ? (
        <div className="product-reviews__loading" aria-hidden="true">
          {[0, 1, 2].map((index) => (
            <div
              key={`review-skeleton-${index}`}
              className="product-review-skeleton product-detail-panel__summary-card"
            >
              <div className="product-review-skeleton__header">
                <span className="product-review-skeleton__avatar" />
                <div className="product-review-skeleton__lines">
                  <span className="product-review-skeleton__line is-short" />
                  <span className="product-review-skeleton__line" />
                </div>
              </div>
              <span className="product-review-skeleton__line" />
              <span className="product-review-skeleton__line" />
              <span className="product-review-skeleton__line is-short" />
            </div>
          ))}
        </div>
      ) : reviewsError ? (
        <div className="product-error product-detail-panel__summary-card">
          <p>{reviewsError}</p>
          <Button
            type="button"
            className="app-button app-button--ghost"
            onClick={() => loadReviews(reviewData.page || 1)}
          >
            {readMessage(messages, 'reviewsList.retry', 'Retry')}
          </Button>
        </div>
      ) : hasVisibleReviews ? (
        <>
          <div className="product-reviews__list">
            {visibleReviews.map((review) => (
              <ReviewCard
                key={review._id || `${review.name}-${review.createdAt}`}
                currentUser={currentUser}
                deleteLabel={deleteActionLabel}
                editLabel={editActionLabel}
                helpfulLabel={helpfulLabel}
                onHelpfulToggle={handleHelpfulToggle}
                pendingHelpfulId={pendingHelpfulId}
                review={review}
              />
            ))}
          </div>

          <ReviewPagination
            currentPage={reviewData.page}
            isLoading={isLoadingReviews}
            messages={messages}
            onPageChange={(pageNumber) => {
              if (!isLoadingReviews && pageNumber !== reviewData.page) {
                loadReviews(pageNumber);
              }
            }}
            pages={reviewData.pages}
          />
        </>
      ) : hasOwnReview ? (
        <article className="product-reviews__status product-detail-panel__summary-card">
          <p className="product-reviews__eyebrow">
            {readMessage(
              messages,
              'reviewsList.onsiteTitle',
              'Customer reviews',
            )}
          </p>
          <h3 className="product-reviews__main-title">
            {readMessage(
              messages,
              'reviewsList.yourReviewTitle',
              'Your review is live',
            )}
          </h3>
          <p className="product-reviews__main-copy">
            {readMessage(
              messages,
              'reviewsList.yourReviewMessage',
              'You are the first DakarGameHub shopper to review this product here.',
            )}
          </p>
        </article>
      ) : null}
    </div>
  ) : null;

  const emptyPanel = (
    <EmptyState
      className="product-empty-state product-detail-panel__summary-card"
      // eyebrow={readMessage(messages, 'reviewsList.title', 'Reviews')}
      icon="✍️"
      message={
        hasExternalReviewSignal
          ? readMessage(
              messages,
              'reviewsList.emptyStateWithSignal',
              'No on-site reviews yet. Be the first to share your experience.',
            )
          : reviewData.message ||
            readMessage(
              messages,
              'writeReview.shareExperience',
              'Share your experience with this product.',
            )
      }
      title={
        onsiteReviewCount > 0
          ? reviewCountCopy
          : readMessage(messages, 'reviews_zero', 'No reviews yet')
      }
    >
      {canWriteReview && !ownReviewState.isReviewed ? (
        <div className="empty-state__actions">
          <Button
            variant="transparent"
            className="app-button app-button--primary"
            onClick={openReviewModal}
          >
            {readMessage(messages, 'writeReview.submit.new', 'Publish review')}
          </Button>
        </div>
      ) : null}
    </EmptyState>
  );

  let reviewsLayout = null;

  const sidebarPanels = [marketplacePanel, actionPanel].filter(Boolean);

  if (hasCustomersSay && hasOnsitePanel) {
    reviewsLayout = (
      <div className="product-reviews__grid product-reviews__grid--legacy">
        {sidebarPanels.length > 0 ? (
          <aside className="product-reviews__sidebar">{sidebarPanels}</aside>
        ) : null}
        {onsitePanel}
      </div>
    );
  } else if (!hasCustomersSay && hasOnsitePanel) {
    reviewsLayout = (
      <div className="product-reviews__grid product-reviews__grid--legacy product-reviews__grid--reviews-first">
        {onsitePanel}
        {actionPanel ? (
          <aside className="product-reviews__sidebar">{actionPanel}</aside>
        ) : null}
      </div>
    );
  } else if (hasCustomersSay) {
    reviewsLayout = (
      <div className="product-reviews__grid product-reviews__grid--legacy">
        {marketplacePanel ? (
          <aside className="product-reviews__sidebar">{marketplacePanel}</aside>
        ) : null}
        {actionPanel ? (
          <aside className="product-reviews__sidebar">{actionPanel}</aside>
        ) : null}
      </div>
    );
  } else if (shouldShowEmptyPanel) {
    reviewsLayout = (
      <div className="product-reviews__grid product-reviews__grid--centered">
        <div className="product-reviews__centered">{emptyPanel}</div>
      </div>
    );
  } else {
    reviewsLayout = null;
  }

  return (
    <section
      ref={sectionRef}
      id="product-reviews"
      className="app-section product-reviews"
    >
      {hasOnsitePanel ? (
        <span className="product-detail-panel__eyebrow">
          {readMessage(messages, 'reviewsList.onsiteTitle', 'Customer reviews')}
        </span>
      ) : null}

      {feedback ? (
        <div
          className={`auth-message auth-message--${feedback.tone === 'error' ? 'error' : 'success'}`}
        >
          {feedback.message}
        </div>
      ) : null}

      {reviewsLayout}

      <ReviewModal
        closeReviewModal={closeReviewModal}
        deleteActionLabel={deleteActionLabel}
        formComment={formComment}
        formError={formError}
        formRating={formRating}
        handleDeleteReview={handleDeleteReview}
        handleSubmit={handleSubmit}
        isModalOpen={isModalOpen}
        isRefreshing={isRefreshing}
        messages={messages}
        ownReviewState={ownReviewState}
        pendingAction={pendingAction}
        setFormComment={setFormComment}
        setFormRating={setFormRating}
      />
    </section>
  );
}
